import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from 'lucide-react'
import { cn } from "@/lib/utils"
import useDialogFocusVisibility from "../../hooks/useDialogFocusVisibility"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "office-dialog-overlay fixed inset-0 z-[200]",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, overlayClassName, style, ...props }, ref) => {
  const [contentNode, setContentNode] = React.useState(null);
  const setContentRef = React.useCallback(node => {
    setContentNode(node);
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);
  useDialogFocusVisibility(contentNode);
  return <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={setContentRef}
      style={{
        // Inline vence animações/transform do CSS que apagam o translate e “somem” o modal
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 210,
        ...style,
      }}
      className={cn(
        "office-dialog grid min-w-0 w-[min(calc(100vw-0.75rem),36rem)] max-w-lg",
        "max-h-[min(92dvh,calc(100dvh-0.75rem))] gap-4 overflow-x-hidden overflow-y-auto touch-manipulation",
        "border border-border bg-card text-foreground",
        "p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]",
        /* Cantos via CSS Office (--office-modal-radius); evita rounded-xl no mobile */
        "rounded-none",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="office-dialog-close absolute right-2.5 top-2.5 inline-flex h-10 w-10 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-muted data-[state=open]:text-muted-foreground sm:right-3 sm:top-3 sm:h-9 sm:w-9">
        <X className="h-4 w-4" />
        <span className="sr-only">Fechar</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("office-dialog-head flex flex-col space-y-1.5 text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "office-dialog-footer flex flex-col-reverse flex-wrap gap-2 border-t pt-4 sm:flex-row sm:justify-end",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

/** Detecta ícone Lucide (ou SVG) como primeiro filho do título → badge Office. */
function isOfficeTitleIcon(node) {
  if (!React.isValidElement(node)) return false
  const type = node.type
  if (type === 'svg') return true
  if (type === 'img' || type === 'picture' || type === 'span' || type === 'div') return false
  if (typeof type === 'string') return false
  const name = String(type?.displayName || type?.name || '')
  if (/Avatar|Image|img/i.test(name)) return false
  const cls = String(node.props?.className || '')
  if (/\blucide\b/.test(cls)) return true
  if (node.props?.strokeWidth != null || node.props?.['stroke-width'] != null) return true
  if (node.props?.size != null && (cls.includes('h-') || cls.includes('w-') || !cls)) return true
  if (/\bh-[345]\b|\bw-[345]\b/.test(cls) && !/rounded-full|avatar/i.test(cls)) return true
  if (/Icon$/i.test(name) || name === 'ForwardRef') return true
  return false
}

const DialogTitle = React.forwardRef(({ className, children, ...props }, ref) => {
  const kids = React.Children.toArray(children).filter((c) => c != null && c !== false)
  const first = kids[0]
  const rest = kids.slice(1)
  const hasIcon = isOfficeTitleIcon(first) && rest.length > 0

  if (hasIcon) {
    const iconEl = React.cloneElement(first, {
      className: undefined,
      size: 20,
      strokeWidth: first.props?.strokeWidth ?? 1.75,
      'aria-hidden': true,
    })
    return (
      <div className="office-dialog-title-row">
        <div className="office-modal-icon" aria-hidden>
          {iconEl}
        </div>
        <DialogPrimitive.Title
          ref={ref}
          className={cn(
            "office-dialog-title min-w-0 flex-1 text-lg font-semibold leading-none tracking-tight",
            className
              ?.replace(/\bflex\b/g, '')
              .replace(/\bitems-center\b/g, '')
              .replace(/\bgap-\d+\b/g, '')
              .replace(/\s+/g, ' ')
              .trim(),
          )}
          {...props}
        >
          {rest}
        </DialogPrimitive.Title>
      </div>
    )
  }

  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("office-dialog-title text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  )
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("office-dialog-desc text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
